<template>
  <div class="v2-dashboard" v-if="ready">
    <div class="container">
      <div class="dashboard-header">
        <div class="project-title">Dashboard</div>
        <DashboardToggle />
      </div>
      <div class="dashboard-content" v-if="currentUser">
        <kit-info-card
          :editable="true"
          :showProfile="showProfile"
          :info="{
            phone_number: formatPhone(currentUser.phone),
            email_address: currentUser.email
          }"
          :title="`${currentUser.first_name} ${currentUser.last_name}`"
          :subTitle="`Joined ${formatDateSimple(currentUser.created_at)}`"
          :userProfile="currentUser"
        />
        <kit-info-card
          v-for="(kit, index) in v2Kits"
          :key="kit.id"
          :info="buildKitInfo(kit)"
          :title="kit.title || `Project ${index + 1}`"
          :kitId="kit.kit_id"
          :status="kit.status"
          @viewKit="goToKit(kit.kit_id)"
        />
        <div class="empty-state" v-if="v2Kits.length === 0">
          <p>No V2 kits found. Activate a kit to get started.</p>
        </div>
        <Profile
          v-if="showCustomerProfile"
          @closeProfile="closeProfile"
        />
        <ActivateNewKit
          v-if="showActivate"
          :newKitModal="newKitModal"
          @resetModal="resetModal"
          @reRenderHome="refreshData"
          @changeHeight="changeHeight"
        />
      </div>
    </div>
  </div>
</template>

<script>
import KitInfoCard from "../components/dashboard/KitInfoCard";
import DashboardToggle from "../components/dashboard/DashboardToggle";
import Profile from "../../components/home/Profile";
import ActivateNewKit from "../../components/home/ActivateNewKit";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "V2Dashboard",
  props: ["showCustomerProfile", "newKitModal"],
  components: { KitInfoCard, DashboardToggle, Profile, ActivateNewKit },
  data() {
    return {
      ready: false,
      showActivate: false,
      height: 630
    };
  },
  computed: {
    ...mapGetters(["currentUser"]),
    v2Kits() {
      if (!this.currentUser || !this.currentUser.kits_v2) return [];
      return [...this.currentUser.kits_v2].sort((a, b) => a.id - b.id);
    }
  },
  methods: {
    ...mapActions(["profile"]),
    buildKitInfo(kit) {
      const info = {};
      if (kit.sampling_date) {
        info.sampling_date = this.formatDate(kit.sampling_date);
      }
      const addr = kit.address;
      if (addr && addr.address && addr.city && addr.state && addr.zip) {
        info.sampling_location = `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}`;
      }
      if (kit.collected_first_name && kit.collected_last_name) {
        info.sampling_collected_by = `${kit.collected_first_name} ${kit.collected_last_name}`;
      }
      return info;
    },
    goToKit(kitId) {
      this.$router.push({ path: `/home-v2/kit/${kitId}` });
    },
    formatDate(date) {
      if (!date) return "00/00/0000";
      const d = new Date(date);
      if (isNaN(d.getTime())) return "00/00/0000";
      return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    },
    formatDateSimple(date) {
      if (!date) return "00/00/0000";
      const formatted = moment(date).format("MM/D/YYYY");
      return formatted === "Invalid date" ? "00/00/0000" : formatted;
    },
    formatPhone(str) {
      if (!str) return null;
      const cleaned = ("" + str).replace(/\D/g, "");
      const match = cleaned.match(/^([1-9]|)?(\d{3})(\d{3})(\d{4})$/);
      if (match) return `(${match[2]}) ${match[3]}-${match[4]}`;
      return null;
    },
    showProfile(val) {
      this.$emit("showProfile", val);
    },
    closeProfile(val) {
      this.$emit("closeProfile", val);
    },
    resetModal(val) {
      this.showActivate = val;
    },
    changeHeight(h) {
      this.height = h + 136;
    },
    async refreshData() {
      this.showActivate = false;
      const user = await this.profile();
      if (user && Object.keys(user).length > 0) {
        this.checkActivatedKits(user);
      }
    },
    checkActivatedKits(user) {
      if (!user.confirmed_phone && !user.confirmed_email) {
        this.$router.replace({ path: "/activate", query: { step: "2" } });
        return;
      }
      const kitsV2 = user.kits_v2 || [];
      const activatedKit = kitsV2.find(k => k.status === "activated");
      if (activatedKit) {
        this.$router.replace({ path: `/home-v2/kit/${activatedKit.kit_id}` });
        return;
      }
      this.ready = true;
    }
  },
  watch: {
    newKitModal() {
      this.showActivate = true;
    }
  },
  mounted() {
    this.refreshData();
    if (localStorage.getItem("actkitid")) {
      this.showActivate = true;
      this.newKitModal.step = 1;
      this.newKitModal.kit_id = localStorage.getItem("actkitid");
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../styles/colors";

.v2-dashboard {
  padding: 130px 0 60px 0;
  background-color: #f4f8f9;
  min-height: calc(100vh - 60px);
  height: auto;
  position: relative;
}

.dashboard-content {
  display: flex;
  flex-direction: column;
  margin-top: 25px;
}

.dashboard-header {
  width: 100%;
  font-family: TTNormsRegular, sans-serif;
  .project-title {
    width: 100%;
    height: 60px;
    background-color: $blue;
    border-radius: 15px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: $white;
    font-size: 28px;
  }
}

.empty-state {
  width: 100%;
  padding: 40px;
  text-align: center;
  font-family: TTNormsRegular, sans-serif;
  color: $gray;
  background-color: $white;
  border-radius: 15px;
  margin: 10px 0;
  box-shadow: 0 3px 15px -9px $boxShadow;
}

@media screen and (max-width: 768px) {
  .v2-dashboard {
    padding: 80px 0 60px 0;
    min-height: calc(100vh - 80px);
  }
}
</style>
